import React from 'react';
import ReactDOM from 'react-dom/client';
import './i18n/i18n';
import 'dayjs/locale/ro';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { Provider } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PersistGate } from 'redux-persist/integration/react';
import { ToastContainer } from 'react-toastify';
import { store, persistor } from './redux/store';
import { configureApi } from './api';
import reportWebVitals from './reportWebVitals';
import App from './App';
import AppErrorBoundary from './components/AppErrorBoundary';
import Venues from './pages/Venues';
import Plays from './pages/Plays';
import Events from './pages/Events';
import EventDetails from './pages/EventDetails';
import Login from './pages/Login';
import Orders from './pages/Orders';
import Statistics from './pages/Statistics';
import Reservations from './pages/Reservations';
import Users from './pages/Users';
import ClientUsers from './pages/ClientUsers';
import PaymentPoints from './pages/PaymentPoints';
import Organizers from './pages/Organizers';
import OrganizerDetails from './pages/OrganizerDetails';
import Payments from './pages/Payments';
import Artists from './pages/Artists';
import VenueDetails from './pages/VenueDetails';
import Scan from './pages/Scan';
import ResetPassword from './pages/ResetPassword';
import Vouchers from './pages/Vouchers';
import VoucherDetails from './pages/VoucherDetails';
import { NotFoundPage, ServerErrorPage } from './pages/ErrorPage';
import RequireAuth from './components/RequireAuth';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'react-toastify/dist/ReactToastify.css';

configureApi({ store, persistor });

const appErrorElement = <ServerErrorPage />;

const router = createBrowserRouter([
  {
    path: '/',
    errorElement: appErrorElement,
    element: (
      <RequireAuth>
        <App />
      </RequireAuth>
    ),
    children: [
      {
        path: 'venues',
        element: (
          <RequireAuth>
            <Venues />
          </RequireAuth>
        ),
      },
      {
        path: 'venues/:venueId',
        element: (
          <RequireAuth>
            <VenueDetails />
          </RequireAuth>
        ),
      },
      {
        path: 'plays',
        element: (
          <RequireAuth>
            <Plays />
          </RequireAuth>
        ),
      },
      {
        path: 'events',
        element: (
          <RequireAuth>
            <Events />
          </RequireAuth>
        ),
      },
      {
        path: 'events/:eventId',
        element: (
          <RequireAuth>
            <EventDetails />
          </RequireAuth>
        ),
      },
      {
        path: 'orders/statistics',
        element: (
          <RequireAuth>
            <Statistics />
          </RequireAuth>
        ),
      },
      {
        path: 'orders',
        element: (
          <RequireAuth>
            <Orders />
          </RequireAuth>
        ),
      },
      {
        path: 'reservations',
        element: (
          <RequireAuth>
            <Reservations />
          </RequireAuth>
        ),
      },
      {
        path: 'users',
        element: (
          <RequireAuth>
            <Users />
          </RequireAuth>
        ),
      },
      {
        path: 'clientUsers',
        element: (
          <RequireAuth>
            <ClientUsers />
          </RequireAuth>
        ),
      },
      {
        path: 'paymentPoints',
        element: (
          <RequireAuth>
            <PaymentPoints />
          </RequireAuth>
        ),
      },
      {
        path: 'organizers',
        element: (
          <RequireAuth>
            <Organizers />
          </RequireAuth>
        ),
      },
      {
        path: 'organizers/:organizerId',
        element: (
          <RequireAuth>
            <OrganizerDetails />
          </RequireAuth>
        ),
      },
      {
        path: 'payments',
        element: (
          <RequireAuth>
            <Payments />
          </RequireAuth>
        ),
      },
      {
        path: 'artists',
        element: (
          <RequireAuth>
            <Artists />
          </RequireAuth>
        ),
      },
      {
        path: 'vouchers',
        element: (
          <RequireAuth>
            <Vouchers />
          </RequireAuth>
        ),
      },
      {
        path: 'vouchers/:voucherId',
        element: (
          <RequireAuth>
            <VoucherDetails />
          </RequireAuth>
        ),
      },
    ],
  },
  { path: '/login', element: <Login />, errorElement: appErrorElement },
  { path: '/scan/:code', element: <Scan />, errorElement: appErrorElement },
  { path: '/reset-password/:code?', element: <ResetPassword />, errorElement: appErrorElement },
  { path: '/500', element: <ServerErrorPage /> },
  { path: '*', element: <NotFoundPage /> },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D53541',
    },
    background: {
      default: '#1E1E1E',
      paper: '#232222',
    },
  },
});

root.render(
  <React.StrictMode>
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <ToastContainer theme="dark" />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <AppErrorBoundary>
              <RouterProvider router={router} />
            </AppErrorBoundary>
          </PersistGate>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log);
